// Per-service FAQ content. Keyed by service slug. Each FAQ becomes both:
//   1. A user-facing accordion on the service page
//   2. A FAQPage JSON-LD entry that Google can use to show rich answers in search results
//
// Best practices applied:
//   - 5–7 questions per service (Google's sweet spot)
//   - Answers are full sentences, 1–3 short paragraphs (Google needs context)
//   - Each answer naturally weaves in the city ("London", "Waterloo") and the clinician name
//     where it fits, for local-SEO juice without sounding stuffed
//   - Genuinely useful answers — these match the kinds of questions patients ask before booking

export const serviceFAQs = {
  "tmd-treatment": [
    {
      question: "What is TMD and how do I know if I have it?",
      answer:
        "TMD (Temporomandibular Disorder) is dysfunction of the jaw joint, the muscles that control it, or both. Common signs include jaw pain or tenderness, clicking or popping noises when chewing or yawning, locking of the jaw, headaches around the temples, ear pain without infection, and difficulty opening your mouth wide. If any of these have been going on for more than a few weeks, a TMD assessment is worthwhile.",
    },
    {
      question: "Is TMD treatment painful?",
      answer:
        "Yes sometimes, however, Treatment is non-invasive and most patients find it relieving rather than uncomfortable. Some manual techniques and intra-oral work can feel firm in tender areas, but Krina always works within your tolerance and checks in continuously. There are no injections, no medications, and no surgical procedures involved.",
    },
    {
      question: "How many sessions will I need?",
      answer:
        "Most patients see meaningful improvement within 3–6 sessions, though chronic or complex cases may need more. After your initial assessment Krina will give you a clear, personalised treatment plan — including how many sessions she estimates and what each will focus on. You're never locked into a package and you can stop at any point.",
    },
    {
      question: "Do I need a referral from my dentist or GP to see Krina?",
      answer:
        "No referral is needed — you can book directly. That said, many of our patients are referred by dentists, oral surgeons, ENT specialists and neurologists, and we work closely with these clinicians where helpful. If you'd like us to liaise with your existing dentist or doctor, just let us know.",
    },
    {
      question: "Will I need an MRI, X-ray or other imaging?",
      answer:
        "Usually no. Most TMDs are diagnosed clinically through history and a thorough physical exam. Krina will only recommend imaging if there's a specific reason — for example, suspected internal joint damage that would change the treatment plan. If imaging is needed, she'll guide you on where to get it.",
    },
    {
      question: "Can TMD be cured permanently or will it come back?",
      answer:
        "Most cases of TMD respond very well to physiotherapy and stay resolved long-term, especially when patients keep up the home exercises Krina prescribes. Habits like jaw clenching, poor posture or high stress can trigger recurrences, which is why a big part of treatment is teaching you how to self-manage symptoms if they ever start to creep back.",
    },
    {
      question: "Does treatment help with jaw clicking or locking?",
      answer:
        "Yes — these are two of the most common reasons patients come to see us. Clicking caused by disc displacement, muscle imbalance or joint hypermobility often improves significantly with the right combination of mobilisations, soft-tissue work and corrective exercises. Locking (where the jaw gets stuck open or closed) is also frequently resolvable with manual therapy.",
    },
  ],

  "headache-migraine-relief": [
    {
      question: "Can physiotherapy really help chronic headaches and migraines?",
      answer:
        "Yes — a growing body of evidence shows that many headaches have a cervical (neck) or jaw component, even when patients describe them as 'migraines'. By assessing and treating the upper cervical spine and TMJ, Krina can dramatically reduce frequency and intensity of attacks. Most patients see meaningful change within 3–4 sessions.",
    },
    {
      question: "What is the Dean Watson Technique?",
      answer:
        "The Dean Watson Technique is a specific manual therapy protocol developed by Dr Dean Watson in Australia for the diagnosis and treatment of cervicogenic headache and migraine. Krina is one of a small number of UK physiotherapists trained in this technique. It involves gentle, sustained pressure on the upper neck joints to identify the precise segments referring pain into the head, then targeted treatment of those specific segments.",
    },
    {
      question: "What's the difference between a tension headache, a migraine and a cervicogenic headache?",
      answer:
        "Tension headaches feel like a tight band around the head, usually muscular. Migraines are typically one-sided, throbbing, often with nausea, light or sound sensitivity. Cervicogenic headaches originate from the upper neck and can mimic both — which is why they're often misdiagnosed. A thorough assessment will identify which type (or combination) you have and the best treatment plan.",
    },
    {
      question: "How many sessions will I need?",
      answer:
        "Most patients require 4–6 sessions to see substantial improvement, though many feel a reduction in symptoms after just 1–2 sessions. Chronic, multi-year cases sometimes need ongoing maintenance every 6–12 weeks. After your initial assessment you'll get a clear plan.",
    },
    {
      question: "Is the treatment safe if I'm on migraine medication?",
      answer:
        "Yes — physiotherapy is complementary to medical care, not a replacement. Many of our patients reduce their reliance on triptans, beta-blockers or other migraine medications as their symptoms improve, but this should always be done in consultation with the GP or neurologist who prescribed them. Krina is happy to coordinate with your medical team.",
    },
    {
      question: "Will I be able to drive home or go back to work after treatment?",
      answer:
        "Yes. Some patients feel mildly tender in the neck for 24–48 hours after manual therapy, but most go straight back to work or continue their day normally. We'll always let you know what to expect after each session.",
    },
  ],

  "tinnitus-management": [
    {
      question: "What is somatic tinnitus?",
      answer:
        "Somatic tinnitus is tinnitus that's driven or modulated by the muscles and joints of the neck, jaw and upper shoulders — rather than purely by inner-ear damage. If your tinnitus changes when you move your jaw, clench your teeth, turn your head, or press on certain neck muscles, there's a strong chance it has a somatic component that physiotherapy can help with.",
    },
    {
      question: "How do I know if my tinnitus is somatic or purely ear-related?",
      answer:
        "Krina performs a specific diagnostic assessment looking at how your tinnitus responds to jaw movement, neck rotation, postural changes and palpation of specific muscle groups. If we can modulate the tinnitus during these tests, it's almost certainly somatic and treatable. If we can't, we'll be honest with you — and refer you on if more appropriate.",
    },
    {
      question: "Will physiotherapy completely cure my tinnitus?",
      answer:
        "For somatic tinnitus, many patients see substantial reductions in loudness, frequency, or intrusiveness — and some experience full resolution. Pure inner-ear tinnitus (e.g. from age-related hearing loss or noise exposure) is harder to treat, but even there, addressing musculoskeletal contributors often makes the tinnitus less bothersome.",
    },
    {
      question: "How long until I notice improvement?",
      answer:
        "Most patients with somatic tinnitus notice a change within 3–4 sessions, though it can take longer with chronic cases. The first noticeable improvement is often that the tinnitus becomes less intrusive or quieter for parts of the day, rather than disappearing all at once.",
    },
    {
      question: "Should I see an ENT specialist first?",
      answer:
        "Generally yes — if you haven't already. An ENT can rule out treatable medical causes (wax buildup, infection, otosclerosis, etc.) and conduct an audiogram. Once those have been excluded, physiotherapy is the next logical step for tinnitus with a musculoskeletal component. Krina works alongside several London ENTs and can coordinate care if helpful.",
    },
    {
      question: "Do I need to stop my current medication?",
      answer:
        "No — physiotherapy doesn't interact with tinnitus medications or supplements. Don't change anything your GP or specialist has prescribed without speaking to them first. Krina will work alongside your existing care, not replace it.",
    },
  ],

  "facial-pain-treatment": [
    {
      question: "What types of facial pain do you treat?",
      answer:
        "Krina treats a wide range of facial pain conditions: muscular and myofascial facial pain, TMJ-referred facial pain, atypical facial pain, post-surgical scar pain, trigeminal nerve symptoms (where physiotherapy is appropriate), and referred pain from the upper cervical spine. If you're not sure whether your facial pain is suitable for physiotherapy, the initial assessment will clarify this.",
    },
    {
      question: "How is this different from medical treatments for trigeminal neuralgia?",
      answer:
        "Trigeminal neuralgia caused by vascular compression of the nerve typically requires medical or surgical management — physiotherapy alone won't cure it. However, many people diagnosed with 'trigeminal neuralgia' actually have referred pain from the neck or jaw that mimics nerve symptoms. Krina can identify whether your pain has a musculoskeletal component and treat that part, often providing significant relief alongside medical care.",
    },
    {
      question: "Will I need imaging like an MRI?",
      answer:
        "Most facial pain conditions are diagnosed clinically. If your symptoms suggest a neurological cause (numbness, weakness, sudden onset), Krina will refer you for appropriate imaging. For musculoskeletal facial pain, imaging is rarely necessary.",
    },
    {
      question: "How long will treatment take?",
      answer:
        "It depends on the cause and how long you've had the pain. Acute facial muscle pain can resolve in 2–3 sessions. Chronic, multi-year facial pain typically takes 6–10 sessions to see meaningful change, sometimes with ongoing maintenance. We'll give you a realistic plan after the initial assessment.",
    },
    {
      question: "Can you treat referred pain from the neck or shoulders?",
      answer:
        "Yes — and it's extremely common. Tight muscles in the upper trapezius, sternocleidomastoid and sub-occipital region routinely refer pain into the temples, cheeks, jaw and behind the eye. Treating these areas can completely resolve what feels like 'facial' pain.",
    },
    {
      question: "Is the treatment painful?",
      answer:
        "Yes sometimes, however, Treatment is non-invasive and most patients find it relieving rather than uncomfortable. Some manual techniques and dry needling can feel firm in tender areas, but Krina always works within your tolerance and checks in continuously. There are no injections, no medications, and no surgical procedures involved.",
    },
  ],

  "massage": [
    {
      question: "How is this different from a regular massage?",
      answer:
        "This isn't a relaxation massage — it's targeted, therapeutic soft-tissue treatment delivered by a fully-trained massage therapist. Mercy uses techniques specifically chosen for jaw, face, neck and shoulder dysfunction, including myofascial release, trigger-point therapy and (where indicated) intra-oral massage. Sessions are 30 or 45 minutes, focused on the muscles driving your symptoms.",
    },
    {
      question: "What is intra-oral massage and is it safe?",
      answer:
        "Intra-oral massage involves wearing gloves and working inside the mouth on the muscles you can't reach externally — the pterygoids, masseter from the inside, and the floor-of-mouth muscles. It's extremely effective for stubborn TMJ tension and jaw pain. It's safe, completely sanitary, and only done with your full consent at every step.",
    },
    {
      question: "Is the massage painful?",
      answer:
        "Yes sometimes, however, most patients find it deeply relieving rather than uncomfortable. Some techniques can feel firm in chronic tension areas when first treated, but Mercy always works within your tolerance and you can pause or stop at any time.",
    },
    {
      question: "How often should I have a session?",
      answer:
        "It depends on your needs. Acute neck/jaw tension may respond to a single session. For chronic patterns, weekly sessions for 3–4 weeks then monthly maintenance is common. Many patients book a session every 4–6 weeks alongside other treatments as a 'reset'.",
    },
    {
      question: "Can I have a massage session during pregnancy?",
      answer:
        "Yes — Mercy is experienced in pregnancy-modified positioning and techniques. We avoid certain pressure points and use side-lying support to keep you comfortable. Let us know you're pregnant when booking so we can plan accordingly.",
    },
    {
      question: "Will it help my TMJ or jaw pain?",
      answer:
        "Yes — targeted jaw and face massage (including intra-oral techniques) is one of the most effective interventions for muscular TMJ pain, particularly in patients who clench or grind. It can be done as a standalone session or combined with full TMD physiotherapy.",
    },
  ],
};

// Build a FAQPage JSON-LD schema from a list of FAQ objects.
// Google uses this to render rich FAQ accordions directly in search results.
export const buildFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": f.answer,
    },
  })),
});
